/** Single confirmation screen of the B2C reschedule flow (PLA-878/881). No personal data. */
import { FC } from "react"
import { Pressable, TextStyle, View, ViewStyle } from "react-native"
import Icon from "react-native-vector-icons/MaterialIcons"

import { Text } from "@/components/Text"

import { ActionButton } from "./ActionButton"
import type { ConfirmData } from "./rescheduleSlotModel"
import { font, tabular, tokens } from "./tokens"

export interface ConfirmViewProps {
  data: ConfirmData
  /** Primary button label; defaults to "Termin verschieben". */
  confirmLabel?: string
  locationName?: string
  isLoading: boolean
  /** Inline error text; null = none. */
  errorMessage: string | null
  /** The chosen time was taken: the primary button leads back to the grid instead. */
  slotTaken: boolean
  onBack: () => void
  onConfirm: () => void
  onPickAnother: () => void
}

export const ConfirmView: FC<ConfirmViewProps> = ({
  data,
  confirmLabel,
  locationName,
  isLoading,
  errorMessage,
  slotTaken,
  onBack,
  onConfirm,
  onPickAnother,
}) => (
  <View style={$wrap}>
    <View style={$hero}>
      <View style={$tile}>
        <Text style={$tileWd}>{data.tile.weekday}</Text>
        <Text style={$tileNum}>{data.tile.dayNumber}</Text>
        <Text style={$tileMonth}>{data.tile.month}</Text>
      </View>
      <View style={$main}>
        <Text style={$fullDate}>{data.fullDate}</Text>
        <Text style={$time}>{data.time} Uhr</Text>
        {data.previous && (
          <Text style={$was}>
            {data.previous.prefix}: <Text style={$wasStruck}>{data.previous.value}</Text>
          </Text>
        )}
        <Pressable
          accessibilityRole="button"
          onPress={onBack}
          disabled={isLoading}
          testID="confirm-change"
          style={$changeWrap}
        >
          <Text style={$change}>Andere Uhrzeit wählen</Text>
        </Pressable>
      </View>
    </View>

    <View style={$list}>
      <Row
        icon="directions-car"
        label="Leistung"
        value={data.serviceTitle}
        small={data.serviceText}
      />
      {locationName && <Row icon="place" label="Prüfstelle" value={locationName} />}
      <Row
        icon="lock-outline"
        label="Fahrzeug & Kontaktdaten"
        value="Wie in Ihrer Buchung"
        small="Wir übernehmen alles unverändert, Sie müssen nichts neu eingeben."
        last
      />
    </View>

    {errorMessage && (
      <View style={$error} accessibilityRole="alert" testID="confirm-error">
        <Icon name="error-outline" size={20} color={tokens.error} />
        <Text style={$errorText}>{errorMessage}</Text>
      </View>
    )}

    <View style={$buttons}>
      <ActionButton label="Zurück" onPress={onBack} disabled={isLoading} testID="confirm-back" />
      {slotTaken ? (
        <ActionButton
          label="Andere Uhrzeit wählen"
          variant="primary"
          onPress={onPickAnother}
          testID="confirm-primary"
        />
      ) : (
        <ActionButton
          label={isLoading ? "Wird gebucht…" : (confirmLabel ?? "Termin verschieben")}
          variant="primary"
          onPress={onConfirm}
          disabled={isLoading}
          loading={isLoading}
          testID="confirm-primary"
        />
      )}
    </View>
  </View>
)

const Row: FC<{ icon: string; label: string; value: string; small?: string; last?: boolean }> = ({
  icon,
  label,
  value,
  small,
  last,
}) => (
  <View style={[$row, !last && $rowDivider]}>
    <Icon name={icon} size={20} color={tokens.ink70} style={$rowIcon} />
    <View style={$rowText}>
      <Text style={$rowLabel}>{label}</Text>
      <Text style={$rowValue}>{value}</Text>
      {small && <Text style={$rowSmall}>{small}</Text>}
    </View>
  </View>
)

const $wrap: ViewStyle = { gap: 16 }
const $hero: ViewStyle = { flexDirection: "row", alignItems: "center", gap: 16 }
const $tile: ViewStyle = {
  width: 72,
  minHeight: 84,
  flexShrink: 0,
  borderRadius: 16,
  backgroundColor: tokens.ink,
  alignItems: "center",
  justifyContent: "center",
  gap: 4,
}
const $tileWd: TextStyle = {
  fontSize: 13,
  lineHeight: 13,
  fontFamily: font.semi,
  color: tokens.surface,
  opacity: 0.75,
}
const $tileNum: TextStyle = {
  fontSize: 30,
  lineHeight: 32,
  fontFamily: font.bold,
  letterSpacing: -0.6,
  color: tokens.surface,
}
const $tileMonth: TextStyle = {
  fontSize: 12,
  lineHeight: 12,
  fontFamily: font.semi,
  color: tokens.surface,
  opacity: 0.75,
}
const $main: ViewStyle = { flex: 1, minWidth: 0, gap: 4 }
const $fullDate: TextStyle = {
  fontSize: 20,
  lineHeight: 24,
  fontFamily: font.bold,
  color: tokens.ink,
}
const $time: TextStyle = { fontSize: 16, fontFamily: font.semi, color: tokens.ink, ...tabular }
const $was: TextStyle = { fontSize: 13, fontFamily: font.regular, color: tokens.ink70 }
const $wasStruck: TextStyle = { color: tokens.ink45, textDecorationLine: "line-through" }
const $changeWrap: ViewStyle = { alignSelf: "flex-start", marginTop: 2 }
const $change: TextStyle = {
  fontSize: 14,
  fontFamily: font.bold,
  color: tokens.ink,
  textDecorationLine: "underline",
}
const $list: ViewStyle = {
  paddingVertical: 4,
  paddingHorizontal: 14,
  borderRadius: 12,
  backgroundColor: tokens.mist,
}
const $row: ViewStyle = {
  flexDirection: "row",
  alignItems: "flex-start",
  gap: 12,
  padding: 12,
  paddingHorizontal: 0,
}
const $rowDivider: ViewStyle = { borderBottomWidth: 1, borderBottomColor: tokens.modalBorder }
const $rowIcon = { marginTop: 1 }
const $rowText: ViewStyle = { flex: 1, minWidth: 0 }
const $rowLabel: TextStyle = {
  fontSize: 12,
  fontFamily: font.semi,
  color: tokens.helper,
  marginBottom: 3,
}
const $rowValue: TextStyle = {
  fontSize: 15,
  lineHeight: 20,
  fontFamily: font.semi,
  color: tokens.ink,
}
const $rowSmall: TextStyle = {
  fontSize: 13,
  marginTop: 2,
  fontFamily: font.medium,
  color: tokens.ink70,
}
const $error: ViewStyle = {
  flexDirection: "row",
  alignItems: "flex-start",
  gap: 10,
  padding: 12,
  borderRadius: 8,
  backgroundColor: tokens.errorBg,
}
const $errorText: TextStyle = {
  flex: 1,
  fontSize: 14,
  lineHeight: 20,
  fontFamily: font.medium,
  color: tokens.error,
}
const $buttons: ViewStyle = { flexDirection: "row", gap: 12 }
